import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowsClockwise,
  Copy,
  Eye,
  EyeSlash,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { mcpTokensQueries } from '../../queries/mcpTokens';
import { McpTokenService } from '../../service/McpTokenService';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import type { McpTokenCreated } from '../../types/mcpToken';
import { User } from '../../util/Types/User';
import KitModal from '../UI/KitModal';

interface Props {
  authUser: User;
}

interface CreateTokenForm {
  name: string;
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : 'Never';

const McpTokens = ({ authUser }: Props) => {
  const workspaceId = authUser?.selectedCompanyId ?? '';
  const queryClient = useQueryClient();
  const setNotification = useNotificationStore(state => state.setNotification);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<McpTokenCreated | null>(
    null,
  );
  const [showToken, setShowToken] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateTokenForm>();

  const tokensQuery = useQuery(mcpTokensQueries.list(workspaceId));

  const invalidateTokens = () =>
    queryClient.invalidateQueries({
      queryKey: mcpTokensQueries.list(workspaceId).queryKey,
    });

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      McpTokenService.create(workspaceId, { name, scopes: ['read'] }),
    onSuccess: response => {
      setCreatedToken(response.data);
      setShowToken(false);
      invalidateTokens();
    },
    onError: error => {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) =>
      McpTokenService.revoke(workspaceId, tokenId),
    onSuccess: () => {
      setNotification({
        type: NotificationType.Success,
        message: 'Token revoked successfully',
      });
      invalidateTokens();
    },
    onError: error => {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    },
  });

  const openCreate = () => {
    reset({ name: '' });
    setCreatedToken(null);
    setShowToken(false);
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setCreatedToken(null);
    setShowToken(false);
    reset({ name: '' });
  };

  const onSubmit = (data: CreateTokenForm) => {
    createMutation.mutate(data.name.trim());
  };

  const copyToken = async () => {
    if (!createdToken?.token) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setNotification({
        type: NotificationType.Success,
        message: 'Token copied to clipboard',
      });
    } catch {
      setNotification({
        type: NotificationType.Error,
        message: 'Failed to copy token',
      });
    }
  };

  const tokens = tokensQuery.data?.data ?? [];

  return (
    <Box sx={{ p: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box>
          <Typography variant='h6'>MCP Tokens</Typography>
          <Typography variant='body2' color='text.secondary'>
            Generate tokens so external AI clients (Claude, Cursor, n8n) can
            read this workspace's agents over the Model Context Protocol.
          </Typography>
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 5 }}
        >
          <IconButton
            size='small'
            onClick={() => tokensQuery.refetch()}
            disabled={tokensQuery.isFetching}
            aria-label='Refresh tokens'
            title='Refresh'
          >
            {tokensQuery.isFetching ? (
              <CircularProgress size={18} />
            ) : (
              <ArrowsClockwise size={18} />
            )}
          </IconButton>
          <Button variant='contained' onClick={openCreate}>
            Generate token
          </Button>
        </Box>
      </Box>

      {tokensQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!tokensQuery.isLoading && tokens.length === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            No MCP tokens yet. Generate one to connect an external AI client.
          </Typography>
        </Box>
      )}

      {!tokensQuery.isLoading && tokens.length > 0 && (
        <Table size='small' sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Authorized by</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map(token => (
              <TableRow key={token.id}>
                <TableCell>{token.name}</TableCell>
                <TableCell>
                  <Chip
                    label={token.kind === 'oauth' ? 'OAuth' : 'Service'}
                    size='small'
                    color={token.kind === 'oauth' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{token.authorizedBy ?? '—'}</TableCell>
                <TableCell>
                  {token.scopes.map(scope => (
                    <Chip
                      key={scope}
                      label={scope}
                      size='small'
                      sx={{ mr: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>{formatDate(token.createdAt)}</TableCell>
                <TableCell>{formatDate(token.lastUsedAt)}</TableCell>
                <TableCell align='right'>
                  <IconButton
                    size='small'
                    color='error'
                    disabled={revokeMutation.isPending}
                    onClick={() => revokeMutation.mutate(token.id)}
                    aria-label={`Revoke ${token.name}`}
                  >
                    <Trash size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <KitModal
        show={isCreateOpen}
        onHide={closeCreate}
        title={createdToken ? 'Token created' : 'Generate MCP token'}
        maxWidth='sm'
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          {!createdToken && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                fullWidth
                label='Token name'
                placeholder='e.g. Claude Desktop'
                {...register('name', { required: 'Name is required' })}
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                sx={{ mb: 2 }}
              />
              <Typography variant='body2' sx={{ mb: 1 }}>
                Scope
              </Typography>
              <Chip label='read' sx={{ mb: 2 }} />
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                Read-only access (list and inspect agents). Write and execute
                scopes will be available in a future release.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={closeCreate} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Generating…' : 'Generate'}
                </Button>
              </Box>
            </form>
          )}

          {createdToken && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  mb: 2,
                  borderRadius: 1,
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText',
                }}
              >
                <Warning size={20} />
                <Typography variant='body2'>
                  Copy this token now. It will not be shown again.
                </Typography>
              </Box>
              <TextField
                fullWidth
                value={createdToken.token}
                type={showToken ? 'text' : 'password'}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() => setShowToken(prev => !prev)}
                        aria-label='Toggle token visibility'
                      >
                        {showToken ? <EyeSlash /> : <Eye />}
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={copyToken}
                        aria-label='Copy token'
                      >
                        <Copy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button variant='contained' onClick={closeCreate}>
                  Done
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </KitModal>
    </Box>
  );
};

export default McpTokens;
